import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, Subject, take } from 'rxjs';
import {
  AuthorizationStatuses,
  IAdverseDeterminationDTO,
  IAuthorizationDTO,
  IMember,
  IMemberAddress,
} from '@core/model';
import { AuthorizationService, NotificationService } from '@core/service';
import { AUTH_STATUS, PRODUCT_CASE_STATUS, PRODUCT_LINE_STATUS } from '@core/constants';
import { ZZ_LINE_CODE } from '@app/feature/auth-request-initial/product-selection/constants';

@Injectable()
export class AuthorizationStateService {
  member$ = new BehaviorSubject<IMember | null>(null);
  authorization$ = new BehaviorSubject<IAuthorizationDTO | null>(null);
  hasNoICDCodes$ = new BehaviorSubject<boolean>(false);

  authorizationLoading$ = new BehaviorSubject(false);

  // State getters
  authorizationId$ = new BehaviorSubject<string | null>(null);
  canSubmit$ = new BehaviorSubject<boolean>(false);
  isReadonly$ = new BehaviorSubject<boolean>(false);

  // Member getters
  memberId$ = new BehaviorSubject<string | null>(null);
  memberAddress$ = new BehaviorSubject<IMemberAddress | null>(null);

  // Adverse Determination for Product Line
  adverseDetermination$ = new BehaviorSubject<IAdverseDeterminationDTO | null>(null);

  existingAdverseDeterminations$ = new BehaviorSubject<Map<string, boolean>>(new Map());

  reloadLetterLog$ = new Subject<void>();

  productId$ = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly notificationService: NotificationService,
  ) {
  }

  setMember(member: IMember) {
    this.member$.next(member);
    this.memberId$.next(member.memberDTO.memberId);
    this.memberAddress$.next(this.getMemberResidentialAddress(member));
  }

  setProductId(productId: string) {
    this.productId$.next(productId);
  }
  
  setAuthorization(authorization: IAuthorizationDTO | null): void {
    this.authorization$.next(authorization);
    this.authorizationId$.next(authorization.authorizationByProduct.authorizationByProductId);
    this.canSubmit$.next(this.checkSubmitAction(authorization));
    this.isReadonly$.next(this.checkIsReadonly(authorization));
  }

  reloadAuthorization(silent?: boolean): void {
    if (!silent) this.authorizationLoading$.next(true)

    this.authorizationService.getAuthorization(this.authorizationId$.value)
      .pipe(
        take(1),
        finalize(() => this.authorizationLoading$.next(false))
      )
      .subscribe({
        next: (res) => {
          this.setAuthorization(res);
          this.isReadonly$.next(this.checkIsReadonly(res));
        },
        error: () => this.notificationService.showError(),
      });
  }

  setAdverseDetermination(adverseDetermination: IAdverseDeterminationDTO): void {
    this.adverseDetermination$.next(adverseDetermination);
  }

  setExistingAdverseDetermination(productLineId: string, result: boolean): void {
    const existingAdverseDeterminations = this.existingAdverseDeterminations$.value;
    existingAdverseDeterminations.set(productLineId, result);
    this.existingAdverseDeterminations$.next(existingAdverseDeterminations);
  }

  checkIfHasInfoAboutAdverseDetermination(productLineId: string): boolean {
    return this.existingAdverseDeterminations$.value.has(productLineId);
  }

  clearExistingAdverseDeterminations(): void {
    this.existingAdverseDeterminations$.next(new Map<string, boolean>());
  }

  /**
   * Check for authorization submit allowance on every authorization update
   * @private
   */
  private checkSubmitAction(authorization: IAuthorizationDTO) {
    const { cases, authorizationByProduct: { authorizationStatus } } = authorization;
    const status = authorizationStatus.description;

    return ([AUTH_STATUS.Pending, AUTH_STATUS.Incomplete] as AuthorizationStatuses[]).includes(status) &&
      cases.every(c => c.status.applicationKey === PRODUCT_CASE_STATUS.Incomplete) &&
      !cases.flatMap((c) => c.productLines)
        .find((p) => p.hcpcsByModifier.hcpcs.code.includes(ZZ_LINE_CODE));
  }

  private checkIsReadonly(authorization: IAuthorizationDTO) {
    const { authorizationByProduct: { authorizationStatus }, cases } = authorization;

    return (
      AUTH_STATUS.Discharged === authorizationStatus.description ||
      (AUTH_STATUS.Cancelled === authorizationStatus.description &&
        cases.length === 1 &&
        cases[0].productLines[0].hcpcsByModifier.hcpcs.code.includes(ZZ_LINE_CODE) &&
        cases[0].productLines[0].status.applicationKey === PRODUCT_LINE_STATUS.Dismissed)
    );
  }

  private getMemberResidentialAddress(member: IMember) {
    return member.memberAddressDTO?.find(a => a.addressType === 'Residential')
  }
}
